import { useState } from "react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import {
  categories,
  trustBadges,
  comparisonData,
  faqs,
  stats,
} from "@/mocks/categories";
import { articlesData } from "@/mocks/articles";

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || "https://kakobuyspreadsheet.icu";
    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is a Kakobuy Spreadsheet?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A Kakobuy Spreadsheet is a curated database of verified fashion products, trusted sellers, and buying guides. It helps shoppers find the best replicas and streetwear items with QC photos, price comparisons, and shipping information all in one organized sheet."
          }
        },
        {
          "@type": "Question",
          "name": "Is the kakobuy spreadsheet free to use?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, the core Kakobuy Spreadsheet is completely free to browse and use. Some advanced filtering features may require a simple registration, but the majority of finds and guides are accessible without any cost."
          }
        },
        {
          "@type": "Question",
          "name": "How often is the kakobuy spreadsheet updated?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We publish updates to the Kakobuy Spreadsheet every Monday and Thursday. Our research team continuously monitors seller reviews, new product drops, and price changes."
          }
        },
        {
          "@type": "Question",
          "name": "Can I trust the sellers listed in the spreadsheet?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "All sellers in our Kakobuy Spreadsheet pass a strict three-step verification process. We check transaction history, buyer feedback, and QC confirmation before adding any seller."
          }
        },
        {
          "@type": "Question",
          "name": "What is the best category for beginners?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We recommend starting with T-Shirts or Accessories in the Kakobuy Spreadsheet. These categories have lower price points, faster shipping times, and a wide variety of options."
          }
        },
        {
          "@type": "Question",
          "name": "Does the kakobuy spreadsheet include shipping guides?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, every category tab in the Kakobuy Spreadsheet includes shipping estimate columns based on recent user data. We also provide comprehensive shipping guides covering different agents, customs tips, and tracking methods."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    const bcScript = document.createElement("script");
    bcScript.type = "application/ld+json";
    bcScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${siteUrl}/`
        }
      ]
    });
    document.head.appendChild(bcScript);

    return () => {
      document.head.removeChild(faqScript);
      document.head.removeChild(bcScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[720px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Premium%20luxury%20streetwear%20fashion%20collection%20spread%20on%20dark%20elegant%20background%20with%20dramatic%20spotlighting%2C%20high-end%20sneakers%20hoodies%20jackets%20arrangement%2C%20soft%20warm%20studio%20lighting%20with%20neutral%20beige%20and%20cream%20tones%2C%20clean%20composition%20with%20breathing%20space&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
          alt="Kakobuy Spreadsheet Hero"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/30" />
        <div className="relative z-10 container-wide py-20 pt-32 md:pt-40">
          <div className="max-w-2xl">
            <h1 className="hero-h1 text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
              Best Kakobuy Spreadsheet 2026
            </h1>
            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-6 max-w-xl">
              Discover the latest Kakobuy Spreadsheet with verified products,
              trusted sellers, shipping guides and safe buying tips.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="btn-primary text-sm md:text-base"
              >
                Kakobuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="btn-accent text-sm md:text-base"
              >
                Buy It Now
              </a>
              <Link
                to="/blog/best-kakobuy-spreadsheet"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white text-sm md:text-base font-medium hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line" />
                View Guides
              </Link>
            </div>
            <div className="flex flex-wrap gap-4 text-white/60 text-xs md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-primary-400" />
                Updated Weekly
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-primary-400" />
                Verified Sellers
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-primary-400" />
                Safe Shopping
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges / Stats */}
      <section className="bg-background-50 py-10 md:py-14">
        <div className="container-wide">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="text-center py-4 md:py-6 px-3 bg-white rounded-xl border border-background-200"
              >
                <p className="text-2xl md:text-3xl font-bold text-primary-600 font-heading mb-1">
                  {stat.number}
                </p>
                <p className="text-xs md:text-sm text-foreground-600">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Categories Grid */}
      <section className="bg-background-100 py-10 md:py-14 lg:py-20">
        <div className="container-wide">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
              Popular Categories
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
              Browse the best finds in the Kakobuy Spreadsheet by category. Each
              link takes you to verified products.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer"
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                    <p className="text-foreground-900 font-semibold text-xs md:text-sm leading-tight">
                      {cat.name}
                    </p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="bg-background-50 py-10 md:py-14 lg:py-20">
        <div className="container-wide">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
              Why Choose Kakobuy Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
              We independently research and verify every seller before adding
              them to the Kakobuy Spreadsheet.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
            {trustBadges.map((badge) => (
              <div
                key={badge.title}
                className="bg-white rounded-xl p-5 md:p-6 border border-background-200 text-center"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className={`${badge.icon} text-primary-600 text-xl md:text-2xl`} />
                </div>
                <h3 className="font-semibold text-sm md:text-base text-foreground-900 mb-1">
                  {badge.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600">
                  {badge.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About / What is Section */}
      <section className="bg-background-100 py-10 md:py-14 lg:py-20">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <span className="text-xs font-medium text-primary-600 uppercase tracking-wider mb-2 block">
                About
              </span>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                What is a Kakobuy Spreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                A Kakobuy Spreadsheet is more than a simple list—it is a curated
                database of trusted fashion products, verified sellers, and
                buying guides. Our team updates this resource weekly so you
                always get the latest prices, QC photos, and warehouse links.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                Unlike random Yupoo links, the Kakobuy Spreadsheet organizes
                items by category, size availability, and price tier. Every
                seller is cross-checked against ratings, buyer feedback, and
                return policies before being added.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-primary text-sm"
                >
                  Browse Spreadsheet
                </a>
                <Link
                  to="/blog/best-kakobuy-spreadsheet"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Read Full Guide
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div className="rounded-xl overflow-hidden h-48 md:h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=Premium%20sneaker%20collection%20close-up%20detail%20shot%20on%20clean%20white%20marble%20surface%20with%20subtle%20veining%2C%20luxury%20streetwear%20footwear%20photography%2C%20soft%20natural%20lighting%20with%20neutral%20warm%20tones%20and%20editorial%20quality&width=600&height=800&seq=about-img-01&orientation=portrait"
                  alt="Verified Sneaker Products"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-xl overflow-hidden h-48 md:h-64 mt-6 md:mt-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Premium%20streetwear%20apparel%20hanging%20display%20in%20modern%20boutique%20setting%20with%20warm%20wood%20accents%2C%20luxury%20fashion%20hoodies%20and%20jackets%20collection%2C%20soft%20warm%20studio%20lighting%20with%20clean%20editorial%20composition&width=600&height=800&seq=about-img-02&orientation=portrait"
                  alt="Fashion Apparel Collection"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="bg-background-50 py-10 md:py-14 lg:py-20">
        <div className="container-wide">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
              Kakobuy Spreadsheet vs Other Lists
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
              See why the Kakobuy Spreadsheet stands out from random seller
              lists.
            </p>
          </div>

          <div className="max-w-3xl mx-auto bg-white rounded-xl border border-background-200 overflow-hidden">
            <div className="grid grid-cols-3 text-xs md:text-sm font-medium border-b border-background-200">
              <div className="px-4 py-3 md:px-6 md:py-4 text-foreground-700">
                Feature
              </div>
              <div className="px-4 py-3 md:px-6 md:py-4 text-primary-600 bg-primary-50/50 text-center">
                Kakobuy Spreadsheet
              </div>
              <div className="px-4 py-3 md:px-6 md:py-4 text-foreground-700 text-center">
                Other Lists
              </div>
            </div>
            {comparisonData.map((row, idx) => (
              <div
                key={row.feature}
                className={`grid grid-cols-3 text-xs md:text-sm ${
                  idx % 2 === 0 ? "bg-background-50/50" : "bg-white"
                }`}
              >
                <div className="px-4 py-3 md:px-6 md:py-4 text-foreground-700 font-medium">
                  {row.feature}
                </div>
                <div className="px-4 py-3 md:px-6 md:py-4 text-center">
                  <span className="inline-flex items-center gap-1 text-primary-600 font-medium">
                    <i className="ri-check-line text-primary-500" />
                    {row.kakobuy}
                  </span>
                </div>
                <div className="px-4 py-3 md:px-6 md:py-4 text-center text-foreground-500">
                  {row.other}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="bg-background-100 py-10 md:py-14 lg:py-20">
        <div className="container-wide">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
              Latest Buying Guides
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
              In-depth articles about the Kakobuy Spreadsheet to help you shop
              smarter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {articlesData.map((article) => (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                className="group bg-white rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <p className="text-xs text-primary-600 font-medium mb-2">
                    {article.date}
                  </p>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    {article.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-background-50 py-10 md:py-14 lg:py-20">
        <div className="container-wide">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
              FAQ About Kakobuy Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
              Common questions about using the Kakobuy Spreadsheet.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl border border-background-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-5 py-4 md:px-6 md:py-5 text-left cursor-pointer"
                >
                  <span className="font-medium text-sm md:text-base text-foreground-900 pr-4">
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-lg text-primary-500 transition-transform shrink-0 ${
                      openFaq === idx ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-4 md:px-6 md:pb-5">
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative py-16 md:py-24 lg:py-32 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Premium%20luxury%20fashion%20items%20spread%20flat%20lay%20on%20dark%20elegant%20surface%20with%20high-end%20streetwear%20collection%20arrangement%2C%20soft%20dramatic%20studio%20lighting%20with%20sophisticated%20moody%20atmosphere%20and%20warm%20tones&width=1600&height=600&seq=cta-bg-01&orientation=landscape"
          alt="Start Exploring"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/40" />
        <div className="relative z-10 container-wide">
          <div className="max-w-xl">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              Start Exploring the Best Kakobuy Spreadsheet Today
            </h2>
            <p className="text-base md:text-lg text-white/80 mb-8 leading-relaxed">
              10,000+ monthly users trust our kakobuy spreadsheet for verified
              sellers and weekly updated finds.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="btn-accent text-sm md:text-base"
              >
                <span className="flex items-center gap-2">
                  Buy It Now
                  <i className="ri-arrow-right-line" />
                </span>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white text-sm md:text-base font-medium hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                Open Spreadsheet
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}